import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Form, Modal, Select, Spin, Typography, message } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { tradeAPI } from '../../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

function Field({ label, value }) {
  const v = value === null || value === undefined || value === '' ? '-' : String(value);
  return (
    <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
      <div style={{ width: 70, color: '#666', flex: '0 0 auto' }}>{label}</div>
      <div style={{ color: '#111', flex: 1, wordBreak: 'break-all' }}>{v}</div>
    </div>
  );
}

export default function MobileMasterDataTopicTradeTables() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [tables, setTables] = useState([]);
  const [topics, setTopics] = useState([]);

  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deletingTable, setDeletingTable] = useState(null);

  const [form] = Form.useForm();

  const filteredTopics = useMemo(() => {
    return (topics || []).filter((topic) => !(tables || []).some((tt) => tt?.topic_id === topic?.id));
  }, [topics, tables]);

  const getTopicName = (topicId) => {
    const topic = (topics || []).find((t) => t?.id === topicId);
    return topic ? topic.name : `话题 ${topicId}`;
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const [tablesResp, topicsResp] = await Promise.all([tradeAPI.getTopicTradeTables(), tradeAPI.getTopics()]);
      setTables(Array.isArray(tablesResp?.data) ? tablesResp.data : []);
      setTopics(Array.isArray(topicsResp?.data) ? topicsResp.data : []);
    } catch (e) {
      message.error('获取列表失败');
      setTables([]);
      setTopics([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const openCreate = () => {
    form.resetFields();
    setIsCreateModalVisible(true);
  };

  const handleCreateOk = async () => {
    try {
      const values = await form.validateFields();
      await tradeAPI.createTopicTradeTable(values);
      message.success('分表创建成功');
      setIsCreateModalVisible(false);
      form.resetFields();
      fetchList();
    } catch (error) {
      message.error(error?.response?.data?.detail || '创建分表失败');
    }
  };

  const handleCreateCancel = () => {
    setIsCreateModalVisible(false);
    form.resetFields();
  };

  const openDelete = (record) => {
    setDeletingTable(record);
    setIsDeleteModalVisible(true);
  };

  const handleDeleteOk = async () => {
    if (!deletingTable) return;
    try {
      await tradeAPI.deleteTopicTradeTable(deletingTable.topic_id);
      message.success('分表删除成功');
      setIsDeleteModalVisible(false);
      setDeletingTable(null);
      fetchList();
    } catch (e) {
      message.error('删除分表失败');
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
    setDeletingTable(null);
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f5f5f5' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '16px 16px 12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <Title level={4} style={{ margin: 0, color: '#fff', fontWeight: 600 }}>
            分表管理
          </Title>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={openCreate}
              style={{
                background: 'rgba(255,255,255,0.2)',
                borderColor: 'rgba(255,255,255,0.3)',
                color: '#fff',
                backdropFilter: 'blur(10px)',
              }}
            >
              新建
            </Button>
            <Button
              size="small"
              onClick={fetchList}
              disabled={loading}
              style={{
                background: 'rgba(255,255,255,0.2)',
                borderColor: 'rgba(255,255,255,0.3)',
                color: '#fff',
                backdropFilter: 'blur(10px)',
              }}
            >
              刷新
            </Button>
            <Button
              size="small"
              onClick={() => navigate('/master-data', { replace: true })}
              style={{
                background: 'rgba(255,255,255,0.2)',
                borderColor: 'rgba(255,255,255,0.3)',
                color: '#fff',
                backdropFilter: 'blur(10px)',
              }}
            >
              返回
            </Button>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '20px 16px 16px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 50 }}>
            <Spin />
          </div>
        ) : (
          <div>
            {(tables || []).map((row, idx) => (
              <Card key={row?.topic_id ?? row?.id ?? idx} style={{ borderRadius: 10, marginBottom: 12 }} styles={{ body: { padding: 12 } }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ fontWeight: 700, color: '#1677ff' }}>{row?.table_name || row?.name || `记录 ${idx + 1}`}</div>
                  <Button size="small" type="link" danger icon={<DeleteOutlined />} onClick={() => openDelete(row)}>
                    删除
                  </Button>
                </div>

                <Field label="话题" value={getTopicName(row?.topic_id || row?.id)} />
                <Field label="表名" value={row?.table_name || row?.name} />
              </Card>
            ))}

            {!tables || tables.length === 0 ? (
              <Card style={{ borderRadius: 10 }} styles={{ body: { padding: 12 } }}>
                <Text type="secondary">暂无数据</Text>
              </Card>
            ) : null}
          </div>
        )}

        <Modal title="新建分表" open={isCreateModalVisible} onOk={handleCreateOk} onCancel={handleCreateCancel}>
          <Form form={form} layout="vertical">
            <Form.Item name="topic_id" label="选择话题" rules={[{ required: true, message: '请选择话题' }]}>
              <Select placeholder="请选择话题">
                {filteredTopics.map((topic) => (
                  <Option key={topic.id} value={topic.id}>
                    {topic.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>

        <Modal title="确认删除" open={isDeleteModalVisible} onOk={handleDeleteOk} onCancel={handleDeleteCancel} okText="确认删除" cancelText="取消" okType="danger">
          <p>确定要删除分表 "{deletingTable?.table_name}" 吗？</p>
          <p style={{ color: 'red', marginTop: '8px' }}>此操作无法撤销，删除后将永久失去该分表及其所有相关数据。</p>
        </Modal>
      </div>
    </div>
  );
}
